import React, {
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import {
  motion,
} from "framer-motion";

import {
  media,
} from "../../server";

import {
  useStateContext,
} from "../../context/ContextProvider";

import AddToOrderModal
  from "../order/AddToOrderModal";

import {
  FiArrowLeft,
  FiArrowRight,
  FiClock,
  FiShoppingBag,
  FiImage,
} from "react-icons/fi";

import {
  FaFireAlt,
  FaUtensils,
} from "react-icons/fa";


function FeaturedMealsCard({
  meals,
  index = 0,
  compact = false,
}) {
  const {
    currentDir,
  } = useStateContext();

  const {
    t,
  } = useTranslation();


  /* =====================================
      STATES
  ====================================== */

  const [
    orderModalOpen,
    setOrderModalOpen,
  ] = useState(false);


  /* =====================================
      DIRECTION
  ====================================== */

  const isRTL =
    currentDir === "rtl";


  /* =====================================
      DATA
  ====================================== */

  const title =
    isRTL
      ? meals?.titleAr
      : meals?.titleEn;


  const description =
    isRTL
      ? (
          meals?.descriptionAr ||
          meals?.textAr
        )
      : (
          meals?.descriptionEn ||
          meals?.textEn
        );


  const categoryTitle =
    isRTL
      ? meals?.categoryId
          ?.titleAr
      : meals?.categoryId
          ?.titleEn;


  const preparationTime =
    meals?.preparationTime
      ? `${meals.preparationTime} ${t(
          "minutes"
        )}`
      : null;


  const hasPrice =
    meals?.price !==
      undefined &&
    meals?.price !==
      null &&
    meals?.price !==
      "";


  const DirectionArrow =
    isRTL
      ? FiArrowLeft
      : FiArrowRight;


  /* =====================================
      HANDLERS
  ====================================== */

  const handleScrollTop =
    () => {

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

    };


  const openOrderModal =
    (event) => {

      event.preventDefault();
      event.stopPropagation();

      setOrderModalOpen(
        true
      );

    };


  return (
    <>
      {/* =====================================
          CARD
      ====================================== */}

      <motion.article
        initial={{
          opacity: 0,
          y: 28,
        }}

        whileInView={{
          opacity: 1,
          y: 0,
        }}

        viewport={{
          once: true,
          amount: 0.12,
        }}

        transition={{
          duration: 0.5,

          delay:
            Math.min(
              index * 0.04,
              0.22
            ),
        }}

        whileHover={{
          y:
            compact
              ? -4
              : -7,
        }}

        className="
          group

          relative

          h-full
          min-w-0
        "
      >

        <div
          className="
            relative

            flex
            flex-col

            h-full

            overflow-hidden

            rounded-[1.25rem]
            md:rounded-[2rem]

            border
            border-color-2/10
            dark:border-white/10

            bg-n-1
            dark:bg-back-darksecondary

            shadow-[0_10px_30px_rgba(68,37,20,0.07)]

            transition-all
            duration-500

            hover:border-color-1/35

            hover:shadow-[0_24px_60px_rgba(68,37,20,0.14)]
          "
        >

          {/* =================================
              IMAGE
          ================================= */}

          <div
            className={`
              relative

              shrink-0

              overflow-hidden

              ${
                compact
                  ? `
                    h-[145px]
                    sm:h-[200px]

                    md:h-[285px]
                  `
                  : `
                    h-[225px]
                    sm:h-[250px]

                    md:h-[285px]
                  `
              }
            `}
          >

            {/* =============================
                IMAGE / FALLBACK
            ============================== */}

            {meals?.Image ? (

              <img
                src={`${media}${meals.Image}`}

                alt={
                  title ||
                  t(
                    "Featured Meal"
                  )
                }

                loading="lazy"

                className="
                  w-full
                  h-full

                  object-cover

                  transition-transform
                  duration-[1000ms]
                  ease-out

                  group-hover:scale-105
                "
              />

            ) : (

              <div
                className="
                  flex

                  w-full
                  h-full

                  items-center
                  justify-center

                  bg-back-secondary
                  dark:bg-back-darkprimery

                  text-color-1/40
                "
              >
                <FiImage
                  className="
                    text-4xl
                    md:text-5xl
                  "
                />
              </div>

            )}


            {/* =============================
                IMAGE SHADE
            ============================== */}

            <div
              className="
                absolute
                inset-0

                bg-gradient-to-t

                from-color-2/65
                via-color-2/[0.04]
                to-transparent

                pointer-events-none
              "
            />


            {/* =================================
                FEATURED BADGE
            ================================= */}

            <div
              className={`
                absolute

                top-3
                start-3

                z-10

                inline-flex

                max-w-[75%]

                items-center

                rounded-full

                bg-color-1

                text-white

                shadow-[0_8px_25px_rgba(68,37,20,0.18)]

                ${
                  compact
                    ? `
                      gap-1

                      px-2
                      py-1.5

                      md:gap-2
                      md:px-3
                      md:py-2
                    `
                    : `
                      gap-1.5

                      px-3
                      py-2
                    `
                }
              `}
            >

              <FaFireAlt
                className={`
                  shrink-0

                  ${
                    compact
                      ? `
                        text-[8px]
                        md:text-xs
                      `
                      : `
                        text-[10px]
                        md:text-xs
                      `
                  }
                `}
              />


              <span
                className={`
                  truncate

                  font-kufi
                  font-medium

                  ${
                    compact
                      ? `
                        text-[7px]
                        md:text-xs
                      `
                      : `
                        text-[9px]
                        md:text-xs
                      `
                  }
                `}
              >
                {t(
                  "Yaman special"
                )}
              </span>

            </div>


            {/* =================================
                CATEGORY
            ================================= */}

            {categoryTitle && (

              <div
                className={`
                  absolute

                  bottom-3
                  start-3

                  z-10

                  flex

                  max-w-[53%]

                  items-center

                  rounded-full

                  border
                  border-white/20

                  bg-color-2/65

                  backdrop-blur-md

                  text-white

                  ${
                    compact
                      ? `
                        gap-1

                        px-2
                        py-1.5

                        md:gap-2
                        md:px-3
                        md:py-2
                      `
                      : `
                        gap-1.5

                        px-3
                        py-2
                      `
                  }
                `}
              >

                <FaUtensils
                  className={`
                    shrink-0

                    text-color-1

                    ${
                      compact
                        ? `
                          text-[7px]
                          md:text-xs
                        `
                        : `
                          text-[9px]
                          md:text-xs
                        `
                    }
                  `}
                />


                <span
                  className={`
                    truncate

                    font-kufi

                    ${
                      compact
                        ? `
                          text-[7px]
                          md:text-xs
                        `
                        : `
                          text-[9px]
                          md:text-xs
                        `
                    }
                  `}
                >
                  {categoryTitle}
                </span>

              </div>

            )}


            {/* =================================
                PRICE
            ================================= */}

            <div
              className={`
                absolute

                bottom-3
                end-3

                z-10

                flex
                items-center

                rounded-full

                border
                border-white/15

                bg-back-primery/95
                dark:bg-color-2/90

                backdrop-blur-md

                shadow-[0_6px_20px_rgba(68,37,20,0.15)]

                ${
                  compact
                    ? `
                      px-2.5
                      py-1.5

                      md:px-4
                      md:py-2
                    `
                    : `
                      px-3.5
                      py-2
                    `
                }
              `}
            >

              {hasPrice ? (

                <div
                  className="
                    flex

                    items-baseline

                    gap-1
                  "
                >

                  <span
                    className={`
                      font-bold

                      text-color-1

                      ${
                        compact
                          ? `
                            text-sm
                            md:text-lg
                          `
                          : `
                            text-base
                            md:text-xl
                          `
                      }
                    `}
                  >
                    {Number(
                      meals.price
                    ).toLocaleString()}
                  </span>


                  <span
                    className={`
                      font-kufi
                      font-medium

                      text-color-2
                      dark:text-white/80

                      ${
                        compact
                          ? `
                            text-[7px]
                            md:text-[10px]
                          `
                          : `
                            text-[8px]
                            md:text-[10px]
                          `
                      }
                    `}
                  >
                    {t(
                      "currency"
                    )}
                  </span>

                </div>

              ) : (

                <span
                  className="
                    font-kufi

                    text-[9px]
                    md:text-[10px]

                    text-color-1
                  "
                >
                  {t(
                    "today price"
                  )}
                </span>

              )}

            </div>


            {/* =================================
                BOTTOM ACCENT
            ================================= */}

            <div
              className="
                absolute

                bottom-0
                start-0

                z-10

                w-14
                md:w-16

                h-[3px]

                bg-color-1

                transition-all
                duration-500

                group-hover:w-full
              "
            />

          </div>


          {/* =================================
              CARD CONTENT
          ================================= */}

          <div
            className={`
              flex
              flex-1
              flex-col

              ${
                compact
                  ? `
                    p-3

                    md:p-6
                  `
                  : `
                    p-4
                    sm:p-5

                    md:p-6
                  `
              }
            `}
          >

            {/* =================================
                SMALL OFFER LABEL
            ================================= */}

            <div
              className={`
                items-center

                gap-1.5

                text-color-1

                ${
                  compact
                    ? `
                      hidden

                      md:flex
                      md:mb-2
                    `
                    : `
                      flex

                      mb-2
                    `
                }
              `}
            >

              <FaFireAlt
                className="
                  text-[9px]
                  md:text-[10px]
                "
              />


              <span
                className="
                  font-kufi

                  text-[8px]
                  md:text-[10px]
                "
              >
                {t(
                  "featured from Yaman"
                )}
              </span>

            </div>


            {/* =================================
                TITLE
            ================================= */}

            <h2
              className={`
                font-kufi
                font-semibold

                text-tcolor-4
                dark:text-tcolor-1

                line-clamp-1

                transition-colors
                duration-300

                group-hover:text-color-1

                ${
                  compact
                    ? `
                      mb-2

                      text-[13px]
                      leading-6

                      sm:text-base

                      md:text-2xl
                      md:leading-normal
                      md:mb-3
                    `
                    : `
                      mb-2.5

                      text-lg
                      leading-8

                      sm:text-xl

                      md:text-2xl
                      md:mb-3
                    `
                }
              `}
            >
              {title}
            </h2>


            {/* =================================
                DESCRIPTION

                عمودان جوال: مخفي
                عمود واحد: ظاهر
                Desktop: ظاهر
            ================================= */}

            {description && (

              <p
                className={`
                  font-kufi

                  text-n-6
                  dark:text-tcolor-2

                  line-clamp-2

                  ${
                    compact
                      ? `
                        hidden

                        md:block
                        md:min-h-[56px]
                        md:mb-5

                        md:text-sm
                        md:leading-7
                      `
                      : `
                        block

                        min-h-[48px]

                        mb-4

                        text-[11px]
                        leading-6

                        sm:text-sm
                        sm:leading-7

                        md:min-h-[56px]
                        md:mb-5
                      `
                  }
                `}
              >
                {description}
              </p>

            )}


            {/* =================================
                PREPARATION TIME
            ================================= */}

            {preparationTime && (

              <div
                className={`
                  items-center

                  gap-1.5

                  text-n-5
                  dark:text-tcolor-3

                  ${
                    compact
                      ? `
                        hidden

                        md:flex
                        md:mb-4
                      `
                      : `
                        flex

                        mb-4
                      `
                  }
                `}
              >

                <div
                  className="
                    flex

                    w-7
                    h-7

                    shrink-0

                    items-center
                    justify-center

                    rounded-full

                    bg-color-1/10

                    text-color-1
                  "
                >

                  <FiClock
                    className="
                      text-xs
                    "
                  />

                </div>


                <span
                  className="
                    font-kufi

                    text-[9px]
                    sm:text-[10px]
                    md:text-xs
                  "
                >
                  {
                    preparationTime
                  }
                </span>

              </div>

            )}


            {/* =================================
                ACTIONS
            ================================= */}

            <div
              className={`
                mt-auto

                flex

                items-center

                gap-2

                border-t
                border-color-2/10
                dark:border-white/10

                ${
                  compact
                    ? `
                      pt-3

                      md:pt-4
                    `
                    : `
                      pt-4
                    `
                }
              `}
            >

              {/* =================================
                  ADD TO ORDER
              ================================= */}

              <motion.button
                type="button"

                whileTap={{
                  scale: 0.96,
                }}

                onClick={
                  openOrderModal
                }

                aria-label={t(
                  "add to order"
                )}

                className={`
                  group/order

                  relative

                  flex
                  flex-1

                  items-center
                  justify-center

                  overflow-hidden

                  rounded-full

                  bg-color-1

                  font-kufi
                  font-semibold

                  text-white

                  shadow-[0_8px_25px_rgba(165,119,58,0.20)]

                  transition-all
                  duration-300

                  hover:bg-color-2

                  ${
                    compact
                      ? `
                        min-h-[38px]

                        gap-1.5

                        px-2

                        text-[8px]

                        sm:min-h-[42px]
                        sm:text-[10px]

                        md:min-h-[46px]
                        md:gap-2
                        md:px-4
                        md:text-sm
                      `
                      : `
                        min-h-[46px]

                        gap-2

                        px-4

                        text-xs
                        md:text-sm
                      `
                  }
                `}
              >

                {/* Hover Decoration */}

                <span
                  className="
                    absolute

                    -start-4

                    w-10
                    h-10

                    rounded-full

                    bg-white/10

                    transition-transform
                    duration-500

                    group-hover/order:scale-[5]
                  "
                />


                <FiShoppingBag
                  className={`
                    relative
                    z-10

                    shrink-0

                    transition-transform
                    duration-300

                    group-hover/order:-rotate-6
                    group-hover/order:scale-110

                    ${
                      compact
                        ? `
                          text-[11px]

                          sm:text-xs

                          md:text-base
                        `
                        : `
                          text-base
                        `
                    }
                  `}
                />


                <span
                  className="
                    relative
                    z-10

                    whitespace-nowrap
                  "
                >
                  {compact
                    ? t("add")
                    : t(
                        "add to order"
                      )}
                </span>

              </motion.button>


              {/* =================================
                  DETAILS
              ================================= */}

              <Link
                to={`/FeaturedMealDetails/${meals?._id}`}

                onClick={
                  handleScrollTop
                }

                aria-label={t(
                  "view Yaman offer"
                )}

                title={t(
                  "view Yaman offer"
                )}

                className={`
                  group/details

                  flex

                  shrink-0

                  items-center
                  justify-center

                  rounded-full

                  border
                  border-color-1/20

                  bg-color-1/5
                  dark:bg-white/5

                  text-color-1

                  transition-all
                  duration-300

                  hover:border-color-1

                  hover:bg-color-1
                  hover:text-white

                  ${
                    compact
                      ? `
                        w-[38px]
                        h-[38px]

                        sm:w-[42px]
                        sm:h-[42px]

                        md:w-[46px]
                        md:h-[46px]
                      `
                      : `
                        w-[46px]
                        h-[46px]
                      `
                  }
                `}
              >

                <DirectionArrow
                  className={`
                    transition-transform
                    duration-300

                    ${
                      isRTL
                        ? `
                          group-hover/details:-translate-x-0.5
                        `
                        : `
                          group-hover/details:translate-x-0.5
                        `
                    }

                    ${
                      compact
                        ? `
                          text-xs
                          md:text-base
                        `
                        : `
                          text-base
                        `
                    }
                  `}
                />

              </Link>

            </div>

          </div>

        </div>

      </motion.article>


      {/* =====================================
          ADD TO ORDER MODAL
      ====================================== */}

      <AddToOrderModal
        isOpen={
          orderModalOpen
        }

        onClose={() =>
          setOrderModalOpen(
            false
          )
        }

        meal={
          meals
        }
      />

    </>
  );
}


export default FeaturedMealsCard;